import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ContentBlockStateValue = Record<string, any>;

export interface ContentBlockStateContextValue {
  /**
   * Read the current persisted state for a content block.
   * Returns an empty object if no state has been set yet.
   */
  getBlockState: (contentBlockId: string) => ContentBlockStateValue;

  /**
   * Merge new values into the persisted state for a content block.
   * Works like React setState — only the provided keys are updated,
   * existing keys not mentioned are preserved.
   */
  setBlockState: (
    contentBlockId: string,
    partialState: ContentBlockStateValue
  ) => void;

  /**
   * Remove all persisted state for a content block.
   * Call this when a content block is permanently removed from the playlist
   * to prevent unbounded memory growth in long-running display sessions.
   */
  clearBlockState: (contentBlockId: string) => void;

  /**
   * Remove all persisted state for all content blocks.
   * Useful when the playlist is fully replaced or the display resets.
   */
  clearAllBlockStates: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ContentBlockStateContext =
  createContext<ContentBlockStateContextValue | null>(null);

ContentBlockStateContext.displayName = "ContentBlockStateContext";

// ─── Provider ─────────────────────────────────────────────────────────────────

export interface ContentBlockStateProviderProps {
  children: React.ReactNode;
}

export const ContentBlockStateProvider: React.FC<
  ContentBlockStateProviderProps
> = ({ children }) => {
  // Use a ref instead of state — updates to block state should not trigger
  // re-renders of the provider tree. Each content block reads its own slice
  // on demand via getBlockState().
  const storeRef = useRef<Record<string, ContentBlockStateValue>>({});

  const getBlockState = useCallback(
    (contentBlockId: string): ContentBlockStateValue => {
      return storeRef.current[contentBlockId] ?? {};
    },
    []
  );

  const setBlockState = useCallback(
    (contentBlockId: string, partialState: ContentBlockStateValue): void => {
      storeRef.current[contentBlockId] = {
        ...storeRef.current[contentBlockId],
        ...partialState,
      };
    },
    []
  );

  const clearBlockState = useCallback((contentBlockId: string): void => {
    delete storeRef.current[contentBlockId];
  }, []);

  const clearAllBlockStates = useCallback((): void => {
    storeRef.current = {};
  }, []);

  return (
    <ContentBlockStateContext.Provider
      value={{
        getBlockState,
        setBlockState,
        clearBlockState,
        clearAllBlockStates,
      }}
    >
      {children}
    </ContentBlockStateContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useContentBlockState = (): ContentBlockStateContextValue => {
  const context = useContext(ContentBlockStateContext);

  if (!context) {
    throw new Error(
      "useContentBlockState must be used within a ContentBlockStateProvider"
    );
  }

  return context;
};